import {
  type ContractMakerExportInput,
  type ContractMakerRenderInput,
  type ContractMakerSectionPresetSaveInput,
  type ContractMakerTemplateSaveInput,
  type ContractMakerTemplateTestCaseSaveInput,
  contractLocaleSchema,
  contractMakerExportInputSchema,
  contractMakerRenderInputSchema,
  contractMakerSectionPresetArchiveInputSchema,
  contractMakerSectionPresetSaveInputSchema,
  contractMakerSectionPresetTypeSchema,
  contractMakerTemplateQaInputSchema,
  contractMakerTemplateSaveInputSchema,
  contractMakerTemplateStatusSchema,
  contractMakerTemplateTestCaseSaveInputSchema,
  validateTemplateDefinition,
} from "@playbrain/contracts"
import { createServerFn } from "@tanstack/react-start"
import * as z from "zod"
import { requireContractMakerPermission } from "../dashboard-permissions.server"
import { requireDashboardSession } from "../dashboard-server-auth"

const listInputSchema = z.object({
  categories: z.array(z.string().trim().min(1).max(128)).max(50).optional(),
  languages: z.array(contractLocaleSchema).max(2).optional(),
  page: z.number().int().positive().optional(),
  pageSize: z.number().int().positive().max(100).optional(),
  status: contractMakerTemplateStatusSchema.optional(),
  query: z.string().optional(),
})

const libraryCategoriesInputSchema = z.object({
  status: contractMakerTemplateStatusSchema.optional(),
})

const generatedDocumentsListInputSchema = z.object({
  templateId: z.string().min(1).optional(),
  limit: z.number().int().positive().max(50).optional(),
})

const sectionPresetsListInputSchema = z.object({
  type: contractMakerSectionPresetTypeSchema.optional(),
})

const templateIdInputSchema = z.object({
  id: z.string().min(1),
})

const templateTestCasesListInputSchema = z.object({
  templateId: z.string().min(1),
})

export const listContractMakerTemplates = createServerFn({ method: "GET" })
  .validator((input: unknown) => listInputSchema.parse(input ?? {}))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "read")
    const { listContractMakerTemplatesData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return listContractMakerTemplatesData(data)
  })

export const listContractMakerLibraryCategories = createServerFn({ method: "GET" })
  .validator((input: unknown) => libraryCategoriesInputSchema.parse(input ?? {}))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "read")
    const { listContractMakerLibraryCategoriesData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return listContractMakerLibraryCategoriesData(data)
  })

export const getContractMakerTemplate = createServerFn({ method: "GET" })
  .validator((input: unknown) => templateIdInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "read")
    const { getContractMakerTemplateData } = await import("@api/features/contract-maker/contractMakerService")
    return getContractMakerTemplateData(data)
  })

export const listContractMakerSectionPresets = createServerFn({ method: "GET" })
  .validator((input: unknown) => sectionPresetsListInputSchema.parse(input ?? {}))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "read")
    const { listContractMakerSectionPresetsData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return listContractMakerSectionPresetsData(data)
  })

export const listGeneratedContractMakerDocuments = createServerFn({ method: "GET" })
  .validator((input: unknown) => generatedDocumentsListInputSchema.parse(input ?? {}))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "read")
    const { listGeneratedContractMakerDocumentsData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return listGeneratedContractMakerDocumentsData(data)
  })

export const saveContractMakerTemplate = createServerFn({ method: "POST" })
  .validator((input: unknown) => parseTemplateSaveInput(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "write")
    const { saveContractMakerTemplateData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return saveContractMakerTemplateData(data as ContractMakerTemplateSaveInput, { userId: userId(user) })
  })

export const saveContractMakerSectionPreset = createServerFn({ method: "POST" })
  .validator((input: unknown) => contractMakerSectionPresetSaveInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "write")
    const { saveContractMakerSectionPresetData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return saveContractMakerSectionPresetData(data as ContractMakerSectionPresetSaveInput, {
      userId: userId(user),
    })
  })

export const archiveContractMakerSectionPreset = createServerFn({ method: "POST" })
  .validator((input: unknown) => contractMakerSectionPresetArchiveInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "archive")
    const { archiveContractMakerSectionPresetData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return archiveContractMakerSectionPresetData(data, { userId: userId(user) })
  })

export const publishContractMakerTemplate = createServerFn({ method: "POST" })
  .validator((input: unknown) => templateIdInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "publish")
    const { publishContractMakerTemplateData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return publishContractMakerTemplateData(data, { userId: userId(user) })
  })

export const archiveContractMakerTemplate = createServerFn({ method: "POST" })
  .validator((input: unknown) => templateIdInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "archive")
    const { archiveContractMakerTemplateData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return archiveContractMakerTemplateData(data, { userId: userId(user) })
  })

export const deleteContractMakerTemplate = createServerFn({ method: "POST" })
  .validator((input: unknown) => templateIdInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "delete")
    const { deleteContractMakerTemplateData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return deleteContractMakerTemplateData(data, { userId: userId(user) })
  })

export const duplicateContractMakerTemplate = createServerFn({ method: "POST" })
  .validator((input: unknown) => templateIdInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "create")
    const { duplicateContractMakerTemplateData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return duplicateContractMakerTemplateData(data, { userId: userId(user) })
  })

export const listContractMakerTemplateTestCases = createServerFn({ method: "GET" })
  .validator((input: unknown) => templateTestCasesListInputSchema.parse(input))
  .handler(async ({ data }) => {
    // Test cases are an authoring surface rather than a reading one: they exist
    // to prove a template renders, and were admin-only before. `write` keeps
    // them with the people who edit templates instead of everyone who can read.
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "write")
    const { listContractMakerTemplateTestCasesData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return listContractMakerTemplateTestCasesData(data)
  })

export const saveContractMakerTemplateTestCase = createServerFn({ method: "POST" })
  .validator((input: unknown) => contractMakerTemplateTestCaseSaveInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "write")
    const { saveContractMakerTemplateTestCaseData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return saveContractMakerTemplateTestCaseData(data as ContractMakerTemplateTestCaseSaveInput, {
      userId: userId(user),
    })
  })

export const deleteContractMakerTemplateTestCase = createServerFn({ method: "POST" })
  .validator((input: unknown) => templateIdInputSchema.parse(input))
  .handler(async ({ data }) => {
    // Deleting a test case edits the template's QA set; `delete` on this
    // resource means destroying a template itself.
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "write")
    const { deleteContractMakerTemplateTestCaseData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return deleteContractMakerTemplateTestCaseData(data, { userId: userId(user) })
  })

export const runContractMakerTemplateQa = createServerFn({ method: "POST" })
  .validator((input: unknown) => contractMakerTemplateQaInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "write")
    const { runContractMakerTemplateQaData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return runContractMakerTemplateQaData(data)
  })

export const renderContractMakerDocument = createServerFn({ method: "POST" })
  .validator((input: unknown) => contractMakerRenderInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "read")
    const { renderContractMakerDocumentData } = await import(
      "@api/features/contract-maker/contractMakerService"
    )
    return renderContractMakerDocumentData(data as ContractMakerRenderInput)
  })

export const createContractMakerExport = createServerFn({ method: "POST" })
  .validator((input: unknown) => contractMakerExportInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireContractMakerPermission(user, "read")
    const input = data as ContractMakerExportInput
    const {
      createContractMakerExportRecordData,
      createGeneratedContractMakerDocumentData,
      getContractMakerTemplateData,
    } = await import("@api/features/contract-maker/contractMakerService")
    const { createContractMakerFilePayloadData } = await import(
      "@api/features/contract-maker/contractMakerExports"
    )
    const template = await getContractMakerTemplateData({ id: input.templateId })
    const generated = await createGeneratedContractMakerDocumentData(input, { userId: userId(user) })
    const payload = await createContractMakerFilePayloadData({
      ...(generated.sections?.footer ? { footerMarkdown: generated.sections.footer } : {}),
      format: input.format,
      locale: input.locale,
      markdown: generated.markdown,
      templateName: template.name[input.locale] || template.name.en || template.id,
    })
    await createContractMakerExportRecordData({
      context: { userId: userId(user) },
      externalUrl: payload.externalUrl ?? null,
      fileName: payload.fileName,
      format: input.format,
      generatedDocumentId: generated.id,
      mimeType: payload.mimeType,
    })
    return { ...payload, generatedDocumentId: generated.id }
  })

function parseTemplateSaveInput(input: unknown) {
  const parsed = contractMakerTemplateSaveInputSchema.parse(input)
  const validation = validateTemplateDefinition(parsed)
  if (!validation.valid) {
    throw new Error(validation.issues.map((issue) => issue.message).join("\n"))
  }
  return parsed
}

function userId(user: unknown): string | null {
  if (!user || typeof user !== "object") return null
  const record = user as Record<string, unknown>
  return stringValue(record.id) ?? stringValue(record.sub) ?? stringValue(record.email)
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null
}
